import { FusionError, serializeFusionError, serializePrettyJsonFusionError } from "@fusion-advantage/backcompat"
import { sqlQuery, sqlRun } from "./ldb"
import { err, ok, Result } from "neverthrow"
import { LdbBlobStorageEncoding, ldbReadBlobData, ldbReadBlobPrettyJson, ldbWriteBlobData } from "./LdbBlobData"
import _ from "lodash"

export enum CloudToDeviceBlobType {
	ApiResponse = 'apiResponse',
	OfflineTable = 'offlineTable',
	Export = 'export',
	Flowmap = 'flowmap',
	Renderer = 'renderer',
	EncounterFile = 'encounterFile',
}

export enum CloudToDeviceBlobApiResponseId {
	AppStatus = 'appStatus',
}

export const CloudToDeviceBlobUpnAll: Readonly<string> = '*'

export interface CloudToDeviceBlobKey {
	type: CloudToDeviceBlobType
	id: string
	upn: string
}

interface CloudToDeviceBlob extends CloudToDeviceBlobKey {
	data: Uint8Array
	etag: string|null
	contentType: string|null
}

interface CloudToDeviceReadBlob extends CloudToDeviceBlob {
	syncedAt: number
}

export const selectCloudToDeviceBlob = async (key: CloudToDeviceBlobKey): Promise<Result<CloudToDeviceReadBlob|null, FusionError>> => {
	let r = await sqlQuery<{
		type: CloudToDeviceBlobType
		id: string
		upn: string
		encoding: LdbBlobStorageEncoding
		etag: string|null
		data: string|Uint8Array
		contentType: string|null
		syncedAt: number
	}>(
		`
			select type, id, upn, encoding, etag, contentType, syncedAt,
			  case when encoding = '${LdbBlobStorageEncoding.B64Bin}' then cast (data as text)
			  else data
			   end as data
			  from cloudToDeviceBlob
			 where type = ?
			   and id = ?
			   and upn = ?
		`,
		[
			key.type,
			key.id,
			key.upn,
		],
	)
	if(r.isErr()) {
		return err(serializeFusionError('selectCloudToDeviceBlob', r.error))
	}
	if(r.value.length < 1) {
		return ok(null)
	}
	const rAccessed = await updateCloudToDeviceBlobAccessed(key)
	if(rAccessed.isErr()) {
		return err(serializeFusionError('selectCloudToDeviceBlob', rAccessed.error))
	}
	const row = r.value[0]
	const rData = ldbReadBlobData(row.data, row.encoding)
	if(rData.isErr()) {
		return err(serializeFusionError('selectCloudToDeviceBlob', rData.error))
	}
	return ok({
		...row,
		data: rData.value,
	})
}

export const selectCloudToDeviceBlobEtag = async (key: CloudToDeviceBlobKey): Promise<Result<string|null, FusionError>> => {
	let r = await sqlQuery<{etag: string|null}>(
		`
			select etag
			  from cloudToDeviceBlob
			 where type = ?
			   and id = ?
			   and upn = ?
		`,
		[
			key.type,
			key.id,
			key.upn,
		],
	)
	if(r.isErr()) {
		return err(serializeFusionError('selectCloudToDeviceBlobEtag', r.error))
	}
	if(r.value.length < 1) {
		return ok(null)
	}
	const rAccessed = await updateCloudToDeviceBlobAccessed(key)
	if(rAccessed.isErr()) {
		return err(serializeFusionError('selectCloudToDeviceBlobEtag', rAccessed.error))
	}
	return ok(r.value[0].etag)
}

export const updateCloudToDeviceBlobAccessed = async (key: CloudToDeviceBlobKey): Promise<Result<void, FusionError>> => {
	let r = await sqlRun(
		`
			update cloudToDeviceBlob
			   set lastAccessedAt = unixepoch()
			 where type = ?
			   and id = ?
			   and upn = ?
		`,
		[
			key.type,
			key.id,
			key.upn,
		],
	)
	if(r.isErr()) {
		return err(serializeFusionError('updateCloudToDeviceBlobAccessed', r.error))
	}
	return ok()
}

interface CloudToDeviceWriteBlob extends CloudToDeviceBlob {
	bin: boolean
}

export const updateCloudToDeviceBlob = async (blob: CloudToDeviceWriteBlob): Promise<Result<void, FusionError>> => {
	const rData = ldbWriteBlobData(blob.data, blob.bin)
	if(rData.isErr()) {
		return err(serializeFusionError('updateCloudToDeviceBlob', rData.error))
	}
	let r = await sqlRun(
		`
			insert into cloudToDeviceBlob (type, id, upn, data, encoding, etag, contentType, syncedAt, lastAccessedAt)
			values (?, ?, ?, ?, ?, ?, ?, unixepoch(), unixepoch())
			on conflict do update set
				data = ?,
				encoding = ?,
				etag = ?,
				contentType = ?,
				syncedAt = unixepoch(),
				lastAccessedAt = unixepoch()
		`,
		[
			blob.type,
			blob.id,
			blob.upn,
			rData.value.data,
			rData.value.encoding,
			blob.etag,
			blob.contentType,
			rData.value.data,
			rData.value.encoding,
			blob.etag,
			blob.contentType,
		],
	)
	if(r.isErr()) {
		return err(serializeFusionError('updateCloudToDeviceBlob', r.error))
	}
	return ok()
}

export const deleteCloudToDeviceBlob = async (key: CloudToDeviceBlobKey): Promise<Result<void, FusionError>> => {
	let r = await sqlRun(
		`
			delete from cloudToDeviceBlob
			 where type = ?
			   and id = ?
			   and upn = ?
		`,
		[
			key.type,
			key.id,
			key.upn,
		],
	)
	if(r.isErr()) {
		return err(serializeFusionError('deleteCloudToDeviceBlob', r.error))
	}
	return ok()
}

//
//
// Database Browser
//

export const selectCloudToDeviceBlobStrKeys = async (): Promise<Result<string[], FusionError>> => {
	let r = await sqlQuery<{key: string}>(
		`
			select concat_ws('|', type, id, upn) as key
			  from cloudToDeviceBlob
		`
	)
	return r.isErr() ? err(r.error) : ok(r.value.map(row => row.key))
}

export const selectCloudToDeviceBlobPreview = async (keyStr: string): Promise<string> => {
	const [type, id, upn] = keyStr.split('|')
	let r = await sqlQuery<{data: string|Uint8Array, encoding: LdbBlobStorageEncoding}>(
		`
			select data, encoding
			  from cloudToDeviceBlob
			 where type = ?
			   and id = ?
			   and upn = ? 
		`,
		[
			type, id, upn,
		],
	)
	if(r.isErr()) {
		return serializePrettyJsonFusionError('selectCloudToDeviceBlobPreview', r.error)
	} else if(r.value.length < 1) {
		return serializePrettyJsonFusionError('selectCloudToDeviceBlobPreview', 'record not found')
	}
	const row = r.value[0]
	return ldbReadBlobPrettyJson(row.data, row.encoding)
}
